import { modalController } from '@ionic/core';
import { Component, Event, EventEmitter, Fragment, h, Prop, State } from '@stencil/core';
import { FileDoc, FileType, SpecificationPageDoc, SpecSection } from 'common';
import { FileService } from '../../global/service/FileService';
import { SpecificationService } from '../../global/service/SpecificationService';
import { ChangeEvent } from 'src/global/ConcourseDb';
import { SpecificationDivision, SpecificationSection } from '../../global';

@Component({
  tag: 'specification-section-selector',
})
export class SpecificationSectionSelector {
  static async present(projectId: string): Promise<SpecSection | null> {
    return new Promise(async resolve => {
      const modal = await modalController.create({
        component: 'specification-section-selector',
        componentProps: { projectId },
        backdropDismiss: false,
        cssClass: 'concourse-modal',
      });
      modal.addEventListener('selected', (e: CustomEvent) => {
        modal.dismiss();
        resolve(e.detail);
      });
      modal.addEventListener('dismiss', () => {
        modal.dismiss();
        resolve(null);
      });
      await modal.present();
    });
  }

  @Prop() projectId: string;

  @Event() selected: EventEmitter<SpecSection>; // Emits the selected section id
  @Event() dismiss: EventEmitter<void>;

  @State() selectedDivision: SpecificationDivision;
  @State() selectedSection: SpecificationSection;
  @State() loading: boolean = true;
  @State() filteredSections: SpecificationSection[];
  @State() errorMessage: string = '';

  private divisions: SpecificationDivision[];
  private fileId: string;
  private docs: SpecificationPageDoc[];

  async componentWillLoad() {
    const db = FileService.getFilesDb(this.projectId);
    db.on('complete', (docs: FileDoc[]) => {
      this.parseDocs(docs, db.awaitingInitialSync);
    });
    db.on('change', async (e: ChangeEvent) => {
      this.parseDocs(await db.getDocs(), db.awaitingInitialSync);
    });
    await this.parseDocs(await db.getDocs(), db.awaitingInitialSync);
  }

  async parseDocs(docs: FileDoc[], awaitingInitialSync: boolean) {
    const file = docs
      .filter(d => d.parent_id != 'trash')
      .sort(FileService.sortByCreatedDateAsc)
      .reverse()
      .find(f => f.type == FileType.SPECIFICATION);
    if (file) {
      this.fileId = file._id;
      this.loadSpecFile();
    } else if (!awaitingInitialSync) {
      this.loading = false;
      this.errorMessage = 'No specification file for this project';
    }
  }

  async loadSpecFile() {
    const db = FileService.getFilePagesDb(this.projectId, this.fileId);
    db.on('complete', (docs: SpecificationPageDoc[]) => {
      this.parsePageDocs(docs, db.awaitingInitialSync);
    });
    this.parsePageDocs(await db.getDocs(), db.awaitingInitialSync);
  }

  parsePageDocs(docs: SpecificationPageDoc[], awaitingInitialSync: boolean) {
    if (docs.length > 0) {
      this.docs = docs;
      const specificationPage = SpecificationService.scanPages(this.docs, []);
      this.divisions = SpecificationService.groupByDivision(specificationPage);
      this.loading = false;
    } else if (!awaitingInitialSync) {
      this.loading = false;
      this.errorMessage = 'No pages for the specification file';
    }
  }

  onSearch(searchTerm: string) {
    if (!searchTerm) {
      this.filteredSections = null;
      return;
    }

    this.selectedDivision = null;
    this.selectedSection = null;
    this.filteredSections = this.divisions
      .flatMap(division => division.sections)
      .filter(section => {
        const title = this.getTitle(section).toLowerCase();
        return title.toLowerCase().includes(searchTerm.toLowerCase());
      });
  }

  selectSection(section: SpecificationSection) {
    this.selectedSection = section;
    const wasSearching = !!this.filteredSections;
    this.selectedDivision = this.divisions.find(division => division.sections.includes(section));

    // Only clear filtered sections if not searching
    if (!wasSearching) {
      this.filteredSections = null;
    }

    // Only scroll if we're not in search mode
    if (!wasSearching) {
      setTimeout(() => {
        document.querySelector('.item.ion-color-primary')?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 500); // slight delay ensures list has re-rendered before scrolling
    }
  }

  handleConfirm() {
    if (this.selectedSection) {
      this.selected.emit({
        fileId: this.fileId,
        num: this.selectedSection.num,
        title: this.getTitle(this.selectedSection),
        firstPageId: this.selectedSection.pages[0]._id,
        lastPageId: this.selectedSection.pages[this.selectedSection.pages.length - 1]._id,
      });
    }
  }

  getTitle(section: SpecificationSection) {
    const title = section.pages.find(p => !!p.title)?.title;
    if (title) return title;
    return section.pages[0].body.split('\n')[0];
  }

  render() {
    const sections = this.filteredSections || this.selectedDivision?.sections;
    const modalTitle = this.selectedDivision ? this.selectedDivision.title : 'Select Division';
    return (
      <Fragment>
        <modal-header
          modalTitle={modalTitle}
          enableSearch={true}
          showBack={!!this.selectedDivision && !this.filteredSections}
          onDismiss={() => this.dismiss.emit()}
          onBack={() => {
            this.selectedDivision = null;
            this.selectedSection = null;
            this.filteredSections = null;
          }}
          onSearchInput={(e: CustomEvent) => this.onSearch(e.detail)}
        />
        <ion-page class="cc-scrollbars concourse-modal-implicit">
          <ion-list class="cc-no-line-list">
            {this.loading ? (
              <div class="ion-text-center" style={{ height: '100%' }}>
                <ion-spinner name="crescent" />
              </div>
            ) : this.errorMessage ? (
              <ion-text class="ion-padding-horizontal">{this.errorMessage}</ion-text>
            ) : this.filteredSections && this.filteredSections.length == 0 ? (
              <ion-text class="ion-padding-horizontal">No sections found.</ion-text>
            ) : sections ? (
              sections.map(section => {
                const division = this.divisions.find(div => div.sections.includes(section));
                return (
                  <selector-item selected={this.selectedSection == section} showCheckmark={true} onItemClick={() => this.selectSection(section)}>
                    <div style={{ flex: '1' }}>
                      <ion-label>
                        {section.num} - {this.getTitle(section)}
                        {this.filteredSections && division && (
                          <p class="ion-text-wrap">
                            {division.num} - {division.title}
                          </p>
                        )}
                      </ion-label>
                    </div>
                  </selector-item>
                );
              })
            ) : (
              this.divisions.map(division => (
                <ion-item button detail={false} onClick={() => (this.selectedDivision = division)}>
                  <ion-label>
                    {division.num} - {division.title}
                  </ion-label>
                  <ion-icon name="chevron-forward-outline" slot="end" color="light" />
                </ion-item>
              ))
            )}
          </ion-list>
        </ion-page>
        <ion-footer>
          <ion-toolbar>
            <ion-button slot="end" shape="round" color="primary" fill="solid" disabled={!this.selectedSection} onClick={() => this.handleConfirm()}>
              Select
            </ion-button>
          </ion-toolbar>
        </ion-footer>
      </Fragment>
    );
  }
}
